import type { ReactNode } from 'react';
import { CircleCheckBig, CircleMinus, OctagonX } from 'lucide-react';

import { Card } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { StatusPill } from '@/components/ui/status-pill';
import { Typography, typographyVariants } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import type { ConfigurationSettingsState } from '@/types/pms.types';
import { landingCopy as copy } from '../landing/copy';
import type { VendorDefinition } from '../vendor-settings/types';
import type { HeroFact, PillDisplay } from './heroModel';

export interface ConnectionCardProps {
  vendor: VendorDefinition;
  pill: PillDisplay;
  /** The message line under the header (paused / inactive), or null. */
  message: string | null;
  facts: HeroFact[];
  /** `sunbeltres.rentvine.com`; null hides the line (Buildium). */
  host: string | null;
  settings: ConfigurationSettingsState;
  /** The settings chip strip; a disabled connection's settings are moot. */
  showSettings: boolean;
  /** The vendor name's heading level: h2 on the landing, h3 under the console's tab heading. */
  headingAs?: 'h2' | 'h3';
  /** Right-aligned header controls. Each page brings its own. */
  actions?: ReactNode;
  /** Extra red callouts under the message (DestructiveNote). */
  notices?: ReactNode;
  /** The sync line under the facts (SyncNotice). */
  syncNotice?: ReactNode;
}

/**
 * A connected PMS vendor's card: logo tile, name, pill, account host, message,
 * facts, sync line and settings chips (`Integrations Redesign` prototype).
 * Props only: no hooks, routes or session reads. The customer landing
 * (`ConnectionHeroCard`) and the internal account page
 * (`AccountConnectionCard`) each wire it and bring their own actions.
 */
export const ConnectionCard = ({
  vendor,
  pill,
  message,
  facts,
  host,
  settings,
  showSettings,
  headingAs = 'h2',
  actions,
  notices,
  syncNotice,
}: ConnectionCardProps) => (
  <Card className="overflow-hidden rounded-xl shadow-none">
    {/* Header row: logo tile · name + pill + account host | actions */}
    <div className="flex flex-wrap items-center gap-5 px-7 py-6">
      <div className="flex h-16 w-[180px] shrink-0 items-center justify-center rounded-lg border border-border bg-background">
        <img src={vendor.logo} alt={vendor.displayName} className="h-[34px] w-[130px] object-contain" />
      </div>
      {/* min() so a narrow column (the console on a phone, ~223 px) never pushes past the card; unchanged at ≥ 260 px. */}
      <div className="flex min-w-[min(100%,260px)] flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2.5">
          <Typography as={headingAs} className="text-xl font-semibold leading-7 text-foreground">
            {vendor.displayName}
          </Typography>
          <StatusPill variant={pill.variant} icon={pill.icon}>{pill.label}</StatusPill>
        </div>
        {host && (
          <Typography as="p" variant="body" className="text-muted-foreground">
            {host}
          </Typography>
        )}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2.5">{actions}</div>}
    </div>

    {message && pill.variant === 'destructive' && <DestructiveNote>{message}</DestructiveNote>}
    {message && pill.variant !== 'destructive' && (
      <Typography as="p" variant="body" className="px-7 pb-5 text-muted-foreground">
        {message}
      </Typography>
    )}

    {notices}

    {facts.length > 0 && (
      <dl className="flex flex-wrap gap-x-12 gap-y-4 px-7 pb-5.5">
        {facts.map((fact) => (
          <div key={fact.label} className="flex flex-col gap-0.5">
            <Typography as="dt" variant="small">{fact.label}</Typography>
            <Typography as="dd" variant="body-semibold">{fact.value}</Typography>
          </div>
        ))}
      </dl>
    )}

    {syncNotice}

    {showSettings && (
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-border bg-background px-7 py-3.5">
        <SettingChip label={copy.hero.chips.autoSync} on={settings.autoSyncAssets} />
        <SettingChip label={copy.hero.chips.watermark} on={settings.watermarkedPhotos} />
      </div>
    )}
  </Card>
);

/** Destructive callout: the Inactive message, and each page's own error notes. */
export const DestructiveNote = ({ children, role }: { children: ReactNode; role?: 'alert' }) => (
  <div
    role={role}
    className="mx-7 mb-5 flex items-start gap-2.5 rounded-lg bg-destructive-soft px-4 py-3 text-destructive-soft-foreground"
  >
    <OctagonX className="mt-px size-4.5 shrink-0 text-destructive" aria-hidden="true" />
    <span className="text-sm leading-5">{children}</span>
  </div>
);

const SYNC_ROW = 'flex items-center gap-2.5 border-t border-border px-7 py-3';
const SYNC_ROW_WRAPPING = 'flex flex-wrap items-center gap-x-2.5 gap-y-1 border-t border-border px-7 py-3';

/** One sync line under the facts: an icon for its kind, then the page's own text (and, for `complete`, its link). */
export const SyncNotice = ({ kind, children }: { kind: 'running' | 'failed' | 'complete'; children: ReactNode }) => (
  <div role="status" className={kind === 'complete' ? SYNC_ROW_WRAPPING : SYNC_ROW}>
    {kind === 'running' && <Spinner size="sm" aria-hidden="true" />}
    {kind === 'failed' && <OctagonX className="h-4 w-4 shrink-0 text-destructive" aria-hidden="true" />}
    {kind === 'complete' && <CircleCheckBig className="h-4 w-4 shrink-0 text-success" aria-hidden="true" />}
    {children}
  </div>
);

/** One "Auto-Sync Assets On" entry in the footer strip: state icon · label · bold state. */
const SettingChip = ({ label, on }: { label: string; on: boolean }) => {
  const Icon = on ? CircleCheckBig : CircleMinus;
  return (
    <span data-testid="setting-chip" className={cn(typographyVariants({ variant: 'body-sm' }), 'inline-flex items-center gap-2')}>
      <Icon
        className={on ? 'h-3.5 w-3.5 text-success' : 'h-3.5 w-3.5 text-muted-foreground'}
        aria-hidden="true"
      />
      <span className="text-foreground">{label}</span>
      <span className="font-semibold text-foreground">{on ? copy.hero.chips.on : copy.hero.chips.off}</span>
    </span>
  );
};
